import {
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
} from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useRef, useState } from "react";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { TableNames } from "../../../../enums/tableNames";
import { extractOrderTrailContextFromRowData } from "../../../../utils/orderTrailComment";
import { MedicalPaOrdersAlgoliaTableColumnKeys } from "../utils/medicalPaOrdersTable";
import { updateTableDataWithSelectAllStatus } from "../utils/updateTableDataWithSelectAllStatus";
import { MedicalPaOrdersState } from "./useMedicalPaOrdersTableData";

/**
 * Manages bulk row selection (select-all and individual toggle) for the medical PA orders table.
 * Tracks which pages have select-all enabled, syncs checkbox state in table data,
 * and propagates selected order IDs upstream via `onDataChange`.
 *
 * Selection reset: rather than resetting whenever `medicalPaOrdersState.data` changes
 * (which would also wipe selection on every infinite-scroll append), this hook resets
 * `pagesForWhichSelectAllIsEnabled` only when the parts of `filterPayload` that change
 * the underlying result set differ from the previous payload — `facet_filters`, `query`,
 * `sort_by`, or `sort_dir`. Page-only changes (next-page fetches) leave the selection intact.
 *
 * @param tableData - Current table row data array
 * @param setTableData - State setter for table row data
 * @param medicalPaOrdersState - Current orders state (used by callers, not for reset)
 * @param onDataChange - Callback invoked with the array of selected order IDs
 * @param filterPayload - Current Postgres search payload; used to detect result-set changes
 * @returns Selection handlers, bulk assign button disabled state, the count of
 *   currently selected orders, page tracking, and admin flag
 */
export const useSelectAll = (
  tableData: any[],
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  medicalPaOrdersState: MedicalPaOrdersState,
  onDataChange: (data: any) => void,
  filterPayload: SearchForMedicalPaOrdersFromPostgresParams | null,
) => {
  const [pagesForWhichSelectAllIsEnabled, setPagesForWhichSelectAllIsEnabled] =
    useState<number[]>([]);
  const [disableBulkAssignButton, setDisableBulkAssignButton] = useState(true);
  const [selectedCount, setSelectedCount] = useState(0);
  const previousFilterPayload =
    useRef<SearchForMedicalPaOrdersFromPostgresParams | null>(null);

  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(
    TableNames.MEDICAL_PA_ORDERS_NEW,
  );

  const isAdmin = isCurrentUserAdmin() || isCurrentUserSuperAdmin();

  // Reset select all state when data changes
  useEffect(() => {
    if (filterPayload) {
      //1. check if the filter payload has changed only on the facet filters OR query OR sort by field OR sort direction
      const isFilterPayloadChanged =
        previousFilterPayload.current?.facet_filters !==
          filterPayload.facet_filters ||
        previousFilterPayload.current?.query !== filterPayload.query ||
        previousFilterPayload.current?.sort_by !== filterPayload.sort_by ||
        previousFilterPayload.current?.sort_dir !== filterPayload.sort_dir;
      if (isFilterPayloadChanged) {
        setPagesForWhichSelectAllIsEnabled([]);
      }
      previousFilterPayload.current = filterPayload;
    }
  }, [filterPayload]);

  // Sync checkbox state when pagesForWhichSelectAllIsEnabled changes
  useEffect(() => {
    if (isAdmin) {
      const startIndex = tableAttributes?.startIndex ?? 0;
      const endIndex = tableAttributes?.endIndex ?? 0;

      setTableData(
        tableData.map((row: any, index: number) => {
          if (index >= startIndex && index <= endIndex) {
            return {
              ...row,
              [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
                ...row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT],
                isChecked: pagesForWhichSelectAllIsEnabled.includes(
                  tableAttributes?.currentStartPage ?? 0,
                ),
              },
            };
          }
          return row;
        }),
      );
    }
  }, [pagesForWhichSelectAllIsEnabled, isAdmin]);

  // Propagate bulk selection data changes
  useEffect(() => {
    if (tableData && tableData.length > 0 && isAdmin) {
      const checkedOrders = tableData
        .filter(
          (row: any) =>
            row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]?.isChecked,
        )
        .map((row: any) =>
          extractOrderTrailContextFromRowData(
            row[MedicalPaOrdersAlgoliaTableColumnKeys.ID],
            row[MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA],
          ),
        );
      onDataChange(checkedOrders);
      setDisableBulkAssignButton(checkedOrders.length === 0);
      setSelectedCount(checkedOrders.length);
    } else {
      onDataChange([]);
      setSelectedCount(0);
    }
  }, [tableData, isAdmin]);

  /** Toggles select-all for the current page, updating checkbox state for all visible rows. */
  const handleSelectAll = () => {
    const currentPage = tableAttributes?.currentStartPage;
    if (currentPage == null) return;

    const latestPageNumbers = pagesForWhichSelectAllIsEnabled.includes(
      currentPage,
    )
      ? pagesForWhichSelectAllIsEnabled.filter((page) => page !== currentPage)
      : [...pagesForWhichSelectAllIsEnabled, currentPage];

    setPagesForWhichSelectAllIsEnabled(latestPageNumbers);
    updateTableDataWithSelectAllStatus(
      isAdmin,
      tableData,
      tableAttributes?.startIndex ?? 0,
      tableAttributes?.endIndex ?? 0,
      currentPage,
      latestPageNumbers,
      setTableData,
    );
  };

  /** Toggles the checkbox for a single row identified by its order ID. */
  const toggleCheckboxSelection = (id: string) => {
    setTableData(
      tableData.map((row: any) => ({
        ...row,
        [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
          ...row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT],
          isChecked:
            id === row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT].id
              ? !row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT].isChecked
              : row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT].isChecked,
        },
      })),
    );
  };

  return {
    handleSelectAll,
    toggleCheckboxSelection,
    disableBulkAssignButton,
    selectedCount,
    pagesForWhichSelectAllIsEnabled,
    setPagesForWhichSelectAllIsEnabled,
    isAdmin,
  };
};
